'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useKanbanStore } from '@/src/modules/workstation/stores/kanban-store'
import { wsConfig } from '@/src/modules/workstation/config/workstation.config'
import { LAYER_COLORS } from '@/src/modules/workstation/data/layer-colors'
import { MOCK_TASKS, MOCK_SUBTASKS } from '@/src/modules/workstation/data/mock-tasks'
import { fetchTasks } from '@/src/modules/workstation/lib/api'
import type { WsTask, TaskStatus } from '@/src/modules/workstation/types'

function formatTimeInSupport(lastMovedAt: string | null): string {
  if (!lastMovedAt) return 'agora'
  const diffMs = Date.now() - new Date(lastMovedAt).getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  if (diffMinutes < 60) return `${diffMinutes} min`
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours}h`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d`
}

export default function SuportePage() {
  const columns = useKanbanStore((s) => s.columns)
  const setTasks = useKanbanStore((s) => s.setTasks)
  const setSubtasks = useKanbanStore((s) => s.setSubtasks)
  const moveTask = useKanbanStore((s) => s.moveTask)
  const setActiveTask = useKanbanStore((s) => s.setActiveTask)
  const [loading, setLoading] = useState(true)

  // Seed store se vazio (mesma lógica das outras páginas)
  useEffect(() => {
    const allTasks = Object.values(columns).flat()
    if (allTasks.length > 0) { setLoading(false); return }

    async function loadTasks() {
      try {
        const tasks = await fetchTasks()
        const storeTasks: WsTask[] = tasks.map((t: Record<string, unknown>) => ({
          id: t.id as string,
          title: t.title as string,
          description: t.description as string,
          layer_id: t.layer_id as string,
          status: (t.status === 'locked' ? 'todo' : t.status) as TaskStatus,
          bloom_level: t.bloom_level,
          cs_score: (t.cs_score as number) || 1,
          xp_reward: t.xp_reward as number,
          estimated_minutes: t.estimated_minutes as number,
          position: t.position as number,
          blocked_by: (t.blocked_by as string[]) || [],
          started_at: t.started_at as string | null,
          completed_at: t.completed_at as string | null,
          last_moved_at: t.last_moved_at as string | null,
          created_at: t.created_at as string,
        }))
        setTasks(storeTasks)
        for (const task of tasks) {
          const subtasks = task.subtasks as { text: string; done: boolean }[]
          if (subtasks?.length > 0) {
            setSubtasks(task.id as string, subtasks)
          }
        }
      } catch {
        setTasks(MOCK_TASKS)
        for (const [taskId, subs] of Object.entries(MOCK_SUBTASKS)) {
          setSubtasks(taskId, subs)
        }
      } finally {
        setLoading(false)
      }
    }

    loadTasks()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const supportTasks = useMemo(() => columns.support ?? [], [columns.support])

  const handleResolve = (taskId: string) => {
    moveTask(taskId, 'support', 'doing', 0)
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-ws-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-ws-on-surface-variant">Carregando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ws-on-surface">Suporte</h1>
          <p className="text-ws-on-surface-variant text-sm mt-1">
            Tarefas que precisam de ajuda ou estão aguardando resposta.
          </p>
        </div>
        <Link
          href="/membros/kanban"
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-full bg-ws-primary text-ws-on-primary hover:bg-ws-primary/90 transition-colors"
        >
          <span className="material-symbols-outlined text-sm">view_kanban</span>
          Voltar ao Kanban
        </Link>
      </div>

      {supportTasks.length === 0 ? (
        <div className="bg-ws-surface-container-lowest p-12 rounded-xl text-center">
          <span className="material-symbols-outlined text-5xl text-ws-outline-variant mb-4">
            check_circle
          </span>
          <h2 className="text-lg font-bold text-ws-on-surface mb-2">Tudo certo por aqui!</h2>
          <p className="text-sm text-ws-on-surface-variant mb-1">Nenhuma tarefa precisando de ajuda.</p>
          <p className="text-xs text-ws-outline">
            Para abrir um ticket, clique em &quot;Preciso de Ajuda&quot; dentro de qualquer tarefa no Kanban.
          </p>
          <Link
            href="/membros/kanban"
            className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 text-sm font-semibold rounded-full bg-ws-surface-container text-ws-on-surface hover:bg-ws-surface-container-high transition-colors"
          >
            <span className="material-symbols-outlined text-sm">arrow_back</span>
            Ir para o Kanban
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Contagem */}
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2.5 h-2.5 rounded-full bg-ws-tertiary" />
            <span className="text-xs font-bold uppercase tracking-widest text-ws-on-surface-variant">
              {supportTasks.length} {supportTasks.length === 1 ? 'ticket aberto' : 'tickets abertos'}
            </span>
          </div>

          {supportTasks.map((task) => {
            const layerInfo = wsConfig.layers.find((l) => l.id === task.layer_id)
            const layerColor = LAYER_COLORS[task.layer_id] ?? {
              bg: 'bg-ws-surface-container',
              text: 'text-ws-on-surface',
            }

            return (
              <div
                key={task.id}
                className="bg-ws-surface-container-lowest p-5 rounded-xl border-l-4 border-ws-tertiary"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${layerColor.bg} ${layerColor.text}`}
                    >
                      {layerInfo?.name ?? 'Camada'}
                    </span>
                    <span className="flex items-center gap-1 text-[10px] text-ws-tertiary font-bold uppercase">
                      <span className="material-symbols-outlined text-xs">schedule</span>
                      Aguardando há {formatTimeInSupport(task.last_moved_at)}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 bg-ws-tertiary-fixed text-ws-tertiary text-[10px] font-bold uppercase rounded-full">
                    Aberto
                  </span>
                </div>

                {/* Título e descrição */}
                <h3 className="text-base font-bold text-ws-on-surface mb-1">{task.title}</h3>
                <p className="text-sm text-ws-on-surface-variant mb-4">{task.description}</p>

                {/* Ações */}
                <div className="flex items-center gap-3 pt-3 border-t border-ws-surface-container">
                  <button
                    onClick={() => handleResolve(task.id)}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-full bg-ws-primary text-ws-on-primary hover:bg-ws-primary/90 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">check</span>
                    Resolvido - Retomar tarefa
                  </button>
                  <button
                    onClick={() => {
                      setActiveTask(task)
                      window.location.href = '/membros/kanban'
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-full border border-ws-outline-variant text-ws-on-surface-variant hover:bg-ws-surface-container transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">open_in_new</span>
                    Abrir tarefa
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
